
<script setup>
import { ref, reactive } from "vue";
import {
  Dialog,
  DialogPanel,
  TransitionChild,
  TransitionRoot,
} from "@headlessui/vue";

const props = defineProps({
  setCursor: { type: Function, required: true },
  isOpen: { type: Boolean, default: false },
});

const emit = defineEmits(["close"]);

function closeModal() {
  emit("close");
}

// Form reactive state
const form = reactive({
  df_name: "",
  df_surname: "",
  df_company: "",
  df_emailaddress: "",
  df_phone: "",
  cf_76: "",
});

// Error messages per field
const errors = reactive({
  df_name: "",
  df_surname: "",
  df_company: "",
  df_emailaddress: "",
  df_phone: "",
});

// Validation functions
function validateEmail(email) {
  const regex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  return regex.test(email);
}

function validatePhone(phone) {
  const regex = /^(\+\d{3}\d{9}|\d{9})$/; // +000XXXXXXXXX or 000000000
  return regex.test(phone);
}

// Main form validation
function validateForm() {
  let valid = true;

  // Reset errors
  Object.keys(errors).forEach((key) => (errors[key] = ""));

  if (!form.df_name) {
    errors.df_name = "Jméno je povinné pole";
    valid = false;
  }
  if (!form.df_surname) {
    errors.df_surname = "Příjmení je povinné pole";
    valid = false;
  }
  if (!form.df_company) {
    errors.df_company = "Společnost je povinné pole";
    valid = false;
  }
  if (!form.df_emailaddress) {
    errors.df_emailaddress = "E-mail je povinné pole";
    valid = false;
  } else if (!validateEmail(form.df_emailaddress)) {
    errors.df_emailaddress = "Neplatná e-mailová adresa";
    valid = false;
  }
  if (!form.df_phone) {
    errors.df_phone = "Telefon je povinné pole";
    valid = false;
  } else if (!validatePhone(form.df_phone)) {
    errors.df_phone = "Telefon musí být ve správném tvaru";
    valid = false;
  }

  return valid;
}

// Handle form submit
function handleSubmit(e) {
  e.preventDefault();

  if (!validateForm()) return;

  // If valid, submit the form to the external action
  const formElement = e.target;
  formElement.submit();
}
</script>

<template>
  <TransitionRoot as="template" :show="isOpen">
    <Dialog
      as="div"
      class="relative z-50"
    >
      <TransitionChild
        as="template"
        enter="ease-out duration-300"
        enter-from="opacity-0"
        enter-to="opacity-100"
        leave="ease-in duration-200"
        leave-from="opacity-100"
        leave-to="opacity-0"
      >
        <div
          class="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity"
        />
      </TransitionChild>

      <div class="fixed inset-0 w-screen overflow-y-auto">
        <div
          class="flex min-h-full items-end justify-center p-4 text-center sm:items-center sm:p-0"
        >
          <TransitionChild
            as="template"
            enter="ease-out duration-300"
            enter-from="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
            enter-to="opacity-100 translate-y-0 sm:scale-100"
            leave="ease-in duration-200"
            leave-from="opacity-100 translate-y-0 sm:scale-100"
            leave-to="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
          >
            <DialogPanel
              class="relative transform overflow-hidden bg-white text-left shadow-xl transition-all sm:my-8 sm:w-full sm:max-w-7xl"
            >
              <div class="modal-bg">
                <div class=" ">
                  <div
                    class="mx-auto flex flex-shrink-0 items-center justify-center sm:mx-0 py-16 px-4"
                  >

									<form
		method="post"
		id="se-webform-21"
 target="_self" class="se-webform-21 se-webform" action="https://app.smartemailing.cz/public/web-forms-v2/display-form/668133-g5jm4bvx8nsxjnalrcicxjl6hs6luq4zrcp0a4d4p2umh3k8rzs61qbwpuhpi7rzf2elf3ueg2csl7oq95ripp0u7huacght4cnk" @submit.prevent="handleSubmit">


	<div class="se-webform-inner">
		<div class="se-header">
			<h3 class="mb-8 text-center">
				{{ $t('club.modal-form.title') }}
			</h3>
		</div>

		<div class="se-body">
			<ul>
	<li class="mb-6">
		<input :placeholder="$t('footer.newsletter.name')" class="footer-input block !bg-transparent !placeholder-black mb-4 py-2 pl-9 pr-3 !shadow-sm focus:outline-none focus:ring-transparent focus:ring-1 sm:text-sm" type="text" v-model="form.df_name" name="fields[df_name]" id="frm-webFormHtmlRenderer-webFormForm-fields-df_name" required data-nette-rules='[{"op":":filled","msg":"jméno je povinné pole"}]'>
    <div v-if="errors.df_name" class="text-red-500 text-sm mt-1">
      {{ errors.df_name }}
    </div>
	</li>
	<li class="mb-6">
		<input :placeholder="$t('footer.newsletter.surname')" class="footer-input block !bg-transparent !placeholder-black mb-4 py-2 pl-9 pr-3 !shadow-sm focus:outline-none focus:ring-transparent focus:ring-1 sm:text-sm" type="text" v-model="form.df_surname" name="fields[df_surname]" id="frm-webFormHtmlRenderer-webFormForm-fields-df_surname" required data-nette-rules='[{"op":":filled","msg":"Příjmení je povinné pole"}]'>
    <div v-if="errors.df_surname" class="text-red-500 text-sm mt-1">
      {{ errors.df_surname }}
    </div>
	</li>
	<li class="mb-6">
		<input :placeholder="$t('form.company')" class="footer-input block !bg-transparent !placeholder-black mb-4 py-2 pl-9 pr-3 !shadow-sm focus:outline-none focus:ring-transparent focus:ring-1 sm:text-sm" type="text" v-model="form.df_company" name="fields[df_company]" id="frm-webFormHtmlRenderer-webFormForm-fields-df_company" required data-nette-rules='[{"op":":filled","msg":"Společnost je povinné pole"}]'>
    <div v-if="errors.df_company" class="text-red-500 text-sm mt-1">
      {{ errors.df_company }}
    </div>
	</li>
	<li class="mb-6">
		<input :placeholder="$t('form.email')" class="footer-input block !bg-transparent !placeholder-black mb-4 py-2 pl-9 pr-3 !shadow-sm focus:outline-none focus:ring-transparent focus:ring-1 sm:text-sm" type="text" v-model="form.df_emailaddress" name="fields[df_emailaddress]" data-emailaddress="1" id="frm-webFormHtmlRenderer-webFormForm-fields-df_emailaddress" required data-nette-rules='[{"op":":filled","msg":"E-mailová adresa je povinné pole."},{"op":":email","msg":"E-mailová adresa je povinné pole."},{"op":"SE20\\Validators\\TypesFormValidator::validateEmailAddress","msg":"E-mailová adresa je povinné pole."}]'>
    <div v-if="errors.df_emailaddress" class="text-red-500 text-sm mt-1">
      {{ errors.df_emailaddress }}
    </div>
	</li>
	<li class="mb-6">
		<input :placeholder="$t('form.phone')" class="footer-input block !bg-transparent !placeholder-black mb-4 py-2 pl-9 pr-3 !shadow-sm focus:outline-none focus:ring-transparent focus:ring-1 sm:text-sm" type="text" v-model="form.df_phone" name="fields[df_phone]" id="frm-webFormHtmlRenderer-webFormForm-fields-df_phone" required data-nette-rules='[{"op":":filled","msg":"Telefon je povinné pole"},{"op":":static","rules":[{"op":"SE20\\Validators\\TypesFormValidator::validatePhoneNumberWithPreprocessing","msg":"Telefon je povinné pole"}],"control":"fields[df_phone]","arg":true}]'>
    <div v-if="errors.df_phone" class="text-red-500 text-sm mt-1">
      {{ errors.df_phone }}
    </div>
	</li>
	<li class="mb-6">
		<textarea :placeholder="$t('form.what-you-expect')" rows="4" class="footer-input block !bg-transparent !placeholder-black mb-4 py-2 pl-9 pr-3 !shadow-sm focus:outline-none focus:ring-transparent focus:ring-1 sm:text-sm" v-model="form.cf_76" name="fields[cf_76]" id="frm-webFormHtmlRenderer-webFormForm-fields-cf_76"></textarea>
	</li>




			</ul>
		</div>

		<div class="se-submit text-center">
			<button class="bg-transparent border rounded-full border-black text-black mt-4 mb-4 px-8 py-2" type="submit" name="_submit" value="Odeslat">{{ $t('eventform.confirm') }}</button>
		</div>

	</div>

<input type="hidden" name="referrer" id="se-ref-field-id" value=""><input type="hidden" name="sessionid" id="se-sessionid-field" value=""><input type="hidden" name="sessionUid" id="se-sessionUid-field" value=""><input type="hidden" name="_do" value="webFormHtmlRenderer-webFormForm-submit"></form>

                	</div>
								</div>
              </div>
              <div
                class="absolute top-0 right-0 text-xl p-4 cursor-pointer"
                @click="closeModal"
              >
                ✖
              </div>
            </DialogPanel>
          </TransitionChild>
        </div>
      </div>
    </Dialog>
  </TransitionRoot>
</template>

<style lang="scss" scoped>
.footer-input {
  border-bottom: 1px solid rgba(0, 0, 0, 1);
  width: 540px;

  @media screen and (max-width: 768px) {
    //width: 450px;
    width: 100%;
    //margin: 0px 12px;
  }
}
</style>


